/*
=============================================
   PER-VIEW HELP PANEL
=============================================
The collapsible "what is this screen for" box at the top of each view.

INJECTED FROM ONE PLACE. shell.js walks every [data-view] after it renders the
page body and prepends one of these where HELP has a matching entry. No view
component was edited to get a panel, and none has to be edited to gain or lose
one: add or remove the key in help-content.js.

A VIEW WITH NO ENTRY RENDERS NOTHING. Not an empty box, not a placeholder. That
is the intended degrade, so a half-written help file leaves the app looking
exactly as it does today rather than sprouting blank panels.

ROLE-SCOPED KEYS. "classes" means different things to a student and a professor,
so the student pages look up "student:<view>" first and fall back to the plain
name. That is why a student on #classes gets the student wording and a professor
on #classes gets theirs, from one file.

<details> DOES THE COLLAPSING. No click handler, no aria wiring, no state
machine: the element is open by default and the browser handles toggling and
keyboard access. JS is used for exactly one thing, remembering the choice.
*/

import { HELP } from "../scripts/help-content.js";

const STORE_PREFIX = "gp.help.";

/* Remembering the collapse is a convenience, never a requirement. Private mode,
   blocked site data and thumbnailing contexts all throw on access, so every
   read and write is wrapped and a failure just means the panel opens. */
function readCollapsed(key) {
   try {
      return window.localStorage.getItem(STORE_PREFIX + key) === "closed";
   } catch {
      return false;
   }
}

function writeCollapsed(key, isCollapsed) {
   try {
      window.localStorage.setItem(STORE_PREFIX + key, isCollapsed ? "closed" : "open");
   } catch {
      /* not being able to remember is not an error worth surfacing */
   }
}

// Text from help-content.js is ours, not user input, but it lands in innerHTML
// alongside the rest of the shell so it gets escaped anyway.
function esc(s) {
   return String(s).replace(/[&<>"]/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]
   ));
}

export function helpEntryFor(view, role) {
   if (!view) return null;
   if (role === "student") {
      const scoped = HELP[`student:${view}`];
      if (scoped) return { entry: scoped, key: `student:${view}` };
   }
   const plain = HELP[view];
   return plain ? { entry: plain, key: view } : null;
}

class AppHelp extends HTMLElement {
   connectedCallback() {
      if (this.dataset.rendered === "1") return;   // guard against a re-render
      const view = this.getAttribute("view");
      const role = this.getAttribute("role") || "student";

      const found = helpEntryFor(view, role);
      if (!found) { this.remove(); return; }

      const { entry, key } = found;
      const collapsed = readCollapsed(key);

      const points = (entry.points || [])
         .map((p) => `<li>${esc(p)}</li>`)
         .join("");

      const more = entry.more
         ? `<p class="help-more"><a href="${esc(entry.more.href)}">${esc(entry.more.text)}</a></p>`
         : "";

      this.innerHTML = `
         <details class="help-panel"${collapsed ? "" : " open"}>
            <summary>
               <span class="help-title">${esc(entry.title)}</span>
               <span class="help-blurb">${esc(entry.blurb)}</span>
            </summary>
            <div class="help-body">
               ${points ? `<ul>${points}</ul>` : ""}
               ${more}
            </div>
         </details>`;

      this.dataset.rendered = "1";

      const det = this.querySelector("details");
      det.addEventListener("toggle", () => writeCollapsed(key, !det.open));
   }
}

customElements.define("app-help", AppHelp);
